import { CustomTargetingWithContextualSegments } from '@openx/components-targeting/Targeting/AdvancedTargeting/CustomTargetingWithContextualSegments';
import { PackageFields } from '@openx/types/package';
import type { CustomVariablesDataPayload } from '@openx/types/targeting/customVariables';
import { TargetingFields } from '@openx/types/targeting/targeting';
import { type ComponentProps, type JSX, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { toCustomTargetingParams } from '../../../utils/toCustomTargetingParams';

type CustomTargetingWithContextualSegmentsFieldProps = ComponentProps<typeof CustomTargetingWithContextualSegments>;

const contextualSegmentsPath = `${PackageFields.TARGETING}.${TargetingFields.CONTEXTUAL_SEGMENTS}`;

/**
 * In the form the card is bound to the custom variables field, so contextual segments are read from the form.
 * The contextual segments field is registered here (as other cards register their own field), so nested forms
 * such as the deal form keep it in sync, e.g. when an account change resets it.
 */
const CustomTargetingWithContextualSegmentsFormField = ({
  targetingParams,
  ...props
}: CustomTargetingWithContextualSegmentsFieldProps): JSX.Element => {
  const { register } = useFormContext();
  const contextual: CustomVariablesDataPayload[] | undefined = useWatch({ name: contextualSegmentsPath });

  const params = useMemo(() => toCustomTargetingParams(targetingParams, contextual), [targetingParams, contextual]);

  return (
    <div {...register(contextualSegmentsPath, { shouldUnregister: true })} style={{ width: '100%' }}>
      <CustomTargetingWithContextualSegments {...props} targetingParams={params} />
    </div>
  );
};

/**
 * Custom Targeting card with contextual segments inside it (standalone contextual segments flag off).
 * Read-only views pass both values in `targetingParams`; the form only provides the custom variables field.
 */
export const CustomTargetingWithContextualSegmentsField = (
  props: CustomTargetingWithContextualSegmentsFieldProps
): JSX.Element =>
  props.readonly ? (
    <CustomTargetingWithContextualSegments {...props} />
  ) : (
    <CustomTargetingWithContextualSegmentsFormField {...props} />
  );
